import React, { useState, useEffect, useRef } from "react";
import api from "../axiosConfig";
import "../css/dcrLeaveApplication.css";
import { BASE_URL } from "../config";
import CustomButton from "./CustomButton";
import {toast} from "react-toastify";

const LeaveApplication = () => {
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const didInit = useRef(false);
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedLeaveType, setSelectedLeaveType] = useState("");
  const [durationType, setDurationType] = useState("single");
  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());
  const [remarks, setRemarks] = useState("Dear Sir/Madam,");
  const [joiningDate, setJoiningDate] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const fetchInitial = async () => {
      try {
        const [statesResp, leaveTypesResp] = await Promise.all([
          api.get("/api/states"),
          api.get("/api/leave-types"),
        ]);

        setStates(statesResp.data || []);
        setLeaveTypes(leaveTypesResp.data || []);
      } catch (err) {
        console.error("Error fetching initial data:", err);
        toast.error("Failed to load initial data. Please try again.");
      }
    };

    fetchInitial();
  }, []);

  // ✅ Full reset function — clears ALL state after successful submit
  const resetForm = () => {
    const today = getCurrentDate();
    setSelectedState("");
    setSelectedDivision("");
    setSelectedEmployee("");
    setDivisions([]);
    setEmployees([]);
    setSelectedLeaveType("");
    setDurationType("single");
    setFromDate(today);
    setToDate(today);
    setRemarks("Dear Sir/Madam,");
    calculateJoiningDate(today);
    setInfoMessage("");
  };

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("");
    setSelectedEmployee("");
    setDivisions([]);
    setEmployees([]);

    if (!stateId) return;

    try {
      const response = await api.get(
        `/api/divisions?state_id=${encodeURIComponent(stateId)}`
      );
      setDivisions(response.data || []);
    } catch (err) {
      console.error("Error fetching divisions:", err);
      toast.error("Failed to load divisions. Please try again.");
    }
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("");
    setEmployees([]);

    if (!divisionId || !selectedState) return;

    try {
      const division = divisions.find(
        (d) => String(d.id) === String(divisionId)
      );
      const divisionName = division?.name;

      if (!divisionName) {
        console.error("Division name not found for ID:", divisionId);
        return;
      }

      const response = await api.get(
        `/api/employees?state_id=${encodeURIComponent(
          selectedState
        )}&division=${encodeURIComponent(divisionName)}`
      );

      setEmployees(response.data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      toast.error("Failed to load employees. Please try again.");
    }
  };

  const generateDateList = (from, to) => {
    const start = new Date(from);
    const end = new Date(to);
    const dates = [];

    const cur = new Date(start);
    while (cur <= end) {
      const formatted = cur.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
      dates.push(formatted);
      cur.setDate(cur.getDate() + 1);
    }

    return dates;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInfoMessage("");

    try {
      if (!selectedEmployee || !selectedLeaveType || !fromDate) {
        setInfoMessage("Please fill all required fields");
        return;
      }

      const leaveData = {
        employee_id: parseInt(selectedEmployee, 10),
        leavetype_id: parseInt(selectedLeaveType, 10),
        start_date: fromDate,
        end_date: durationType === "multiple" ? toDate : fromDate,
        joining_date: joiningDate,
        empremarks: remarks,
        duration: generateDateList(
          fromDate,
          durationType === "multiple" ? toDate : fromDate
        ).join(", "),
      };

      const response = await api.post("/api/leave-applications", leaveData);

      console.log("✅ Server Response:", response.data);
      toast.success("Leave application submitted successfully!");

      // ✅ Full clean reset — all dropdowns + fields cleared
      resetForm();
    } catch (error) {
      console.error("❌ Submission error:", error);
      const msg =
        (typeof error?.response?.data?.detail === "string"
          ? error.response.data.detail
          : "") ||
        error?.response?.data?.message ||
        "Failed to submit leave application";

      setInfoMessage(msg);
    }
  };

  const calculateJoiningDate = (endDate) => {
    if (!endDate) return;

    const date = new Date(endDate);
    date.setDate(date.getDate() + 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    setJoiningDate(`${year}-${month}-${day}`);
  };

  useEffect(() => {
    const today = getCurrentDate();
    setFromDate(today);
    setToDate(today);
    calculateJoiningDate(today);
  }, []);

  const handleDurationChange = (type) => {
    setDurationType(type);
    if (type === "single") {
      setToDate(fromDate);
      calculateJoiningDate(fromDate);
    }
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    if (durationType === "single") {
      setToDate(date);
      calculateJoiningDate(date);
    } else {
      calculateJoiningDate(toDate);
    }
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    calculateJoiningDate(date);
  };

  return (
    <div className="leave-application-page">
      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Employee </span>
          <span className="text-blue-500">Leave Application</span>
        </h1>

        {infoMessage && (
          <div className="relative flex items-start gap-3 p-4 mb-4 border border-yellow-500 bg-yellow-100 rounded-lg shadow-md animate-fadeIn">
            <span className="text-yellow-700 text-xl">⚠️</span>
            <p className="text-yellow-800 font-semibold">{infoMessage}</p>
            <button
              onClick={() => setInfoMessage("")}
              className="absolute right-3 top-2 text-yellow-700 hover:text-red-500 text-lg font-bold"
            >
              ✖
            </button>
          </div>
        )}

        <section className="p-5 border border-gray-300 rounded-lg shadow-sm bg-white space-y-5">
          <div className="grid gap-5 md:grid-cols-3">
            {/* STATE */}
            <div className="flex items-center gap-3">
              <label className="font-semibold w-28 text-gray-700">State</label>
              <select
                value={selectedState}
                onChange={(e) => handleStateChange(e.target.value)}
                className="border p-2 rounded-lg flex-1"
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* DIVISION */}
            <div className="flex items-center gap-3">
              <label className="font-semibold w-28 text-gray-700">
                Division
              </label>
              <select
                disabled={!selectedState}
                value={selectedDivision}
                onChange={(e) => handleDivisionChange(e.target.value)}
                className="border p-2 rounded-lg disabled:bg-gray-100 flex-1"
              >
                <option value="">Select Division</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* EMPLOYEE */}
            <div className="flex items-center gap-3">
              <label className="font-semibold w-28 shrink-0 text-gray-700">
                Employee
              </label>
              <div className="w-[370px]">
                <select
                  disabled={!selectedDivision}
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full border p-2 rounded-lg disabled:bg-gray-100"
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} | {emp.employee} (
                      {emp.designation} | {emp.headquarter_name} | {emp.division})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        <div className="bg-white border-2 border-gray-300 rounded-lg p-5 mt-4 mb-5 shadow-md">
          <div className="leave-details-fields">
            <div className="leave-details-column">
              <div className="leave-duration-options">
                <label className="required-label">Duration</label>
                <div className="leave-radio-group">
                  <label>
                    <input
                      type="radio"
                      name="duration"
                      checked={durationType === "single"}
                      onChange={() => handleDurationChange("single")}
                      required
                    />
                    Single Day
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="duration"
                      checked={durationType === "multiple"}
                      onChange={() => handleDurationChange("multiple")}
                    />
                    Multiple Days
                  </label>
                </div>
              </div>

              <div className="leave-date-field">
                <label className="required-label">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  className="leave-date-input"
                  required
                />
              </div>
            </div>

            <div className="leave-details-column">
              <div className="flex flex-row gap-5">
                <label className="required-label">Leave Type</label>
                <select
                  value={selectedLeaveType}
                  onChange={(e) => setSelectedLeaveType(e.target.value)}
                  className="border p-2 rounded-lg w-[22rem]"
                >
                  <option value="">Select Leave</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="leave-date-field mt-8">
                <label className="required-label">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => handleToDateChange(e.target.value)}
                  disabled={durationType === "single"}
                  className="border p-2 rounded-lg w-[22rem]"
                  required
                />
              </div>
            </div>
          </div>

          <div className="leave-remarks-field gap-4">
            <label>Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="leave-remarks-input"
              rows="4"
            />
          </div>

          <div className="leave-joining-date mt-2">
            <input
              type="date"
              value={joiningDate}
              onChange={(e) => setJoiningDate(e.target.value)}
              className="leave-joining-input"
            />
          </div>

          <div className="leave-action-buttons">
            <CustomButton
              label="Submit"
              onClick={handleSubmit}
              className="leave-submit-button"
            />
            {/* ✅ Return button also resets the form */}
            <CustomButton
              label="Return"
              onClick={resetForm}
              className="leave-return-button"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveApplication;